import { useCallback, useState } from 'react';
import { useSnackbarStore } from '../../../shared/stores/useSnackbarStore';
import { updateValidationSettingsGrpc } from '../../../shared/api/grpc/validationGrpcClient';
import {
  DEFAULT_VALIDATION_RUNTIME_SETTINGS,
  normalizeValidationRuntimeSettings,
  type ValidationRuntimeSettings,
} from '../lib/validationRuntimeSettings';
import { USE_GRPC } from '../lib/validationRuntimeEnv';
import type { ValidationRunCoordinator } from '../lib/validationRunCoordinator';
import type { PcfValidationReport } from '../types';

export interface UseValidationSettingsOptions {
  coordinator?: ValidationRunCoordinator<PcfValidationReport>;
}

export interface UseValidationSettingsReturn {
  validationSettings: ValidationRuntimeSettings;
  setValidationSettings: React.Dispatch<React.SetStateAction<ValidationRuntimeSettings>>;
  validationSettingsSaving: boolean;
  handleUpdateReconnectGapMargin: (marginMillimeters: number) => Promise<void>;
}

export function useValidationSettings(
  options: UseValidationSettingsOptions = {}
): UseValidationSettingsReturn {
  const { coordinator } = options;
  const snackbar = useSnackbarStore();
  const [validationSettings, setValidationSettings] = useState<ValidationRuntimeSettings>(
    DEFAULT_VALIDATION_RUNTIME_SETTINGS
  );
  const [validationSettingsSaving, setValidationSettingsSaving] = useState(false);

  const handleUpdateReconnectGapMargin = useCallback(
    async (marginMillimeters: number) => {
      const nextMargin = Number(marginMillimeters);
      const minMargin = validationSettings.minReconnectGapMarginMillimeters;
      const maxMargin = validationSettings.maxReconnectGapMarginMillimeters;

      if (!Number.isFinite(nextMargin) || nextMargin < minMargin || nextMargin > maxMargin) {
        snackbar.showError(
          `Reconnect margin must be between ${minMargin.toFixed(1)} mm and ${maxMargin.toFixed(1)} mm`
        );
        return;
      }

      setValidationSettingsSaving(true);
      try {
        if (USE_GRPC) {
          const result = await updateValidationSettingsGrpc(nextMargin);
          if (!result.success) {
            snackbar.showError(result.error || 'Failed to update reconnect margin');
            return;
          }

          setValidationSettings((current) => normalizeValidationRuntimeSettings(result, current));
        } else {
          setValidationSettings((current) =>
            normalizeValidationRuntimeSettings(
              { reconnectGapMarginMillimeters: nextMargin },
              current
            )
          );
        }

        coordinator?.invalidateAll();
        snackbar.showSuccess(`Reconnect margin set to ${nextMargin.toFixed(1)} mm`);
      } catch (error: any) {
        snackbar.showError(error.message || 'Failed to update reconnect margin');
      } finally {
        setValidationSettingsSaving(false);
      }
    },
    [
      snackbar,
      validationSettings.minReconnectGapMarginMillimeters,
      validationSettings.maxReconnectGapMarginMillimeters,
      coordinator,
    ]
  );

  return {
    validationSettings,
    setValidationSettings,
    validationSettingsSaving,
    handleUpdateReconnectGapMargin,
  };
}
